/**
 * Visual Object Editor Store
 *
 * Zustand store for managing the visual object editor state.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { IDFDocument, IDFObject } from '../parser/types';
import type { IDDSchema, IDDObjectType } from '../idd/types';
import type {
  ObjectFilter,
  ViewMode,
  ObjectTreeNode,
  ObjectValidation,
  WizardStep,
} from './types';
import { validateObject } from './validation';
import { getGroupOrder } from './types';

interface VisualObjectEditorState {
  // View state
  viewMode: ViewMode;
  selectedObjectIds: string[];
  expandedGroups: Set<string>;
  expandedClasses: Set<string>;
  filter: ObjectFilter;

  // Wizard state
  wizardOpen: boolean;
  wizardStep: WizardStep;
  wizardSelectedClass: string | null;
  wizardFieldValues: Record<number, string>;

  // Validation cache (computed on demand)
  autoValidate: boolean;

  // Actions - Selection
  selectObject: (id: string, multi?: boolean) => void;
  selectObjects: (ids: string[]) => void;
  clearSelection: () => void;
  selectAll: (document: IDFDocument | null) => void;

  // Actions - View
  setViewMode: (mode: ViewMode) => void;
  toggleGroup: (groupName: string) => void;
  toggleClass: (className: string) => void;
  expandAllGroups: (groups: string[]) => void;
  collapseAllGroups: () => void;

  // Actions - Filter
  setFilter: (filter: Partial<ObjectFilter>) => void;
  clearFilter: () => void;

  // Actions - Wizard
  openWizard: (preset?: string) => void;
  closeWizard: () => void;
  setWizardStep: (step: WizardStep) => void;
  setWizardClass: (className: string | null) => void;
  setWizardFieldValue: (fieldIndex: number, value: string) => void;
  resetWizard: () => void;

  // Actions - Settings
  setAutoValidate: (enabled: boolean) => void;

  // Helpers
  buildObjectTree: (
    document: IDFDocument | null,
    schema: IDDSchema | null
  ) => ObjectTreeNode[];
  getFilteredObjects: (
    document: IDFDocument | null,
    schema: IDDSchema | null
  ) => IDFObject[];
  getValidationForObject: (
    object: IDFObject,
    classInfo: IDDObjectType | undefined,
    document: IDFDocument | null,
    schema: IDDSchema | null
  ) => ObjectValidation;
}

const defaultFilter: ObjectFilter = {
  searchQuery: '',
  showOnlyInvalid: false,
  showOnlyWarnings: false,
  groupFilter: null,
  classFilter: null,
};

/**
 * JSON replacer function to handle Set objects
 */
function setReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Set) {
    return { __type: 'Set', values: Array.from(value) };
  }
  return value;
}

/**
 * JSON reviver function to restore Set objects
 */
function setReviver(_key: string, value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    (value as Record<string, unknown>)['__type'] === 'Set' &&
    Array.isArray((value as Record<string, unknown>)['values'])
  ) {
    return new Set((value as { values: unknown[] })['values']);
  }
  return value;
}

export const useVisualObjectEditorStore = create<VisualObjectEditorState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        viewMode: 'cards',
        selectedObjectIds: [],
        expandedGroups: new Set(['Simulation Parameters', 'Thermal Zones and Surfaces', 'Internal Gains']),
        expandedClasses: new Set(),
        filter: defaultFilter,

        wizardOpen: false,
        wizardStep: 'class',
        wizardSelectedClass: null,
        wizardFieldValues: {},

        autoValidate: true,

        // Selection actions
        selectObject: (id, multi = false) => {
          set((state) => ({
            selectedObjectIds: multi
              ? state.selectedObjectIds.includes(id)
                ? state.selectedObjectIds.filter((i) => i !== id)
                : [...state.selectedObjectIds, id]
              : [id],
          }));
        },

        selectObjects: (ids) => {
          set({ selectedObjectIds: ids });
        },

        clearSelection: () => {
          set({ selectedObjectIds: [] });
        },

        selectAll: (document) => {
          if (!document) return;
          const filtered = get().getFilteredObjects(document, null);
          set({ selectedObjectIds: filtered.map((o) => o.id) });
        },

        // View actions
        setViewMode: (mode) => {
          set({ viewMode: mode });
        },

        toggleGroup: (groupName) => {
          set((state) => {
            const next = new Set(state.expandedGroups);
            if (next.has(groupName)) {
              next.delete(groupName);
            } else {
              next.add(groupName);
            }
            return { expandedGroups: next };
          });
        },

        toggleClass: (className) => {
          set((state) => {
            const next = new Set(state.expandedClasses);
            if (next.has(className)) {
              next.delete(className);
            } else {
              next.add(className);
            }
            return { expandedClasses: next };
          });
        },

        expandAllGroups: (groups) => {
          set({ expandedGroups: new Set(groups) });
        },

        collapseAllGroups: () => {
          set({ expandedGroups: new Set() });
        },

        // Filter actions
        setFilter: (filterUpdate) => {
          set((state) => ({
            filter: { ...state.filter, ...filterUpdate },
          }));
        },

        clearFilter: () => {
          set({ filter: defaultFilter });
        },

        // Wizard actions
        openWizard: (preset) => {
          set({
            wizardOpen: true,
            wizardStep: 'class',
            wizardSelectedClass: preset ?? null,
            wizardFieldValues: {},
          });
        },

        closeWizard: () => {
          set({ wizardOpen: false });
        },

        setWizardStep: (step) => {
          set({ wizardStep: step });
        },

        setWizardClass: (className) => {
          set({
            wizardSelectedClass: className,
            wizardFieldValues: {},
          });
        },

        setWizardFieldValue: (fieldIndex, value) => {
          set((state) => ({
            wizardFieldValues: {
              ...state.wizardFieldValues,
              [fieldIndex]: value,
            },
          }));
        },

        resetWizard: () => {
          set({
            wizardOpen: false,
            wizardStep: 'class',
            wizardSelectedClass: null,
            wizardFieldValues: {},
          });
        },

        // Settings
        setAutoValidate: (enabled) => {
          set({ autoValidate: enabled });
        },

        // Helper: Build object tree from document
        buildObjectTree: (document, schema) => {
          if (!document || !schema) return [];

          const groups = new Map<string, ObjectTreeNode>();
          const classNodes = new Map<string, ObjectTreeNode>();

          // First pass: create nodes for all objects
          for (const obj of document.objects) {
            const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
            const groupName = classInfo?.group || 'Uncategorized';

            // Create group node if needed
            if (!groups.has(groupName)) {
              groups.set(groupName, {
                id: `group-${groupName}`,
                type: 'group',
                name: groupName,
                validationStatus: 'valid',
                children: [],
                objectCount: 0,
              });
            }

            const groupNode = groups.get(groupName);
            if (!groupNode) continue; // Should never happen, but guard against it

            const classKey = `${groupName}::${obj.className}`;

            // Create class node if needed
            if (!classNodes.has(classKey)) {
              const classNode: ObjectTreeNode = {
                id: `class-${obj.className}-${groupName}`,
                type: 'class',
                name: obj.className,
                validationStatus: 'valid',
                children: [],
                objectCount: 0,
                objectClass: obj.className,
              };
              classNodes.set(classKey, classNode);
              // Ensure children array exists before pushing
              if (!groupNode.children) groupNode.children = [];
              groupNode.children.push(classNode);
            }

            const classNode = classNodes.get(classKey);
            if (!classNode) continue; // Should never happen, but guard against it

            classNode.objectCount = (classNode.objectCount || 0) + 1;
            groupNode.objectCount = (groupNode.objectCount || 0) + 1;

            // Validate and add object node
            const validation = get().getValidationForObject(obj, classInfo, document, schema);
            // Ensure children array exists for counting
            const childCount = classNode.children?.length ?? 0;
            const objectName =
              obj.fields[0]?.value ||
              `${obj.className} ${childCount + 1}`;

            // Ensure children array exists before pushing
            if (!classNode.children) classNode.children = [];
            classNode.children.push({
              id: obj.id,
              type: 'object',
              name: objectName,
              validationStatus: validation.status,
              objectClass: obj.className,
              objectData: obj,
            });

            // Propagate validation status up
            if (validation.status === 'error') {
              classNode.validationStatus = 'error';
              groupNode.validationStatus = 'error';
            } else if (
              validation.status === 'warning' &&
              classNode.validationStatus !== 'error'
            ) {
              classNode.validationStatus = 'warning';
              if (groupNode.validationStatus !== 'error') {
                groupNode.validationStatus = 'warning';
              }
            }
          }

          // Sort groups by order
          const sortedGroups = Array.from(groups.values()).sort(
            (a, b) => getGroupOrder(a.name) - getGroupOrder(b.name)
          );

          // Sort class nodes alphabetically within each group
          for (const group of sortedGroups) {
            if (group.children) {
              group.children.sort((a, b) => a.name.localeCompare(b.name));
            }
          }

          return sortedGroups;
        },

        // Helper: Get filtered objects
        getFilteredObjects: (document, schema) => {
          if (!document) return [];

          const { filter } = get();
          let objects = document.objects;

          // Filter by search query
          if (filter.searchQuery.trim()) {
            const query = filter.searchQuery.toLowerCase();
            objects = objects.filter(
              (obj) =>
                obj.className.toLowerCase().includes(query) ||
                obj.fields.some((f) => f.value.toLowerCase().includes(query))
            );
          }

          // Filter by group
          if (filter.groupFilter && schema) {
            objects = objects.filter((obj) => {
              const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
              return classInfo?.group === filter.groupFilter;
            });
          }

          // Filter by class
          if (filter.classFilter) {
            objects = objects.filter(
              (obj) =>
                obj.className.toLowerCase() === filter.classFilter!.toLowerCase()
            );
          }

          // Filter by validation status
          if ((filter.showOnlyInvalid || filter.showOnlyWarnings) && schema) {
            objects = objects.filter((obj) => {
              const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
              const validation = get().getValidationForObject(
                obj,
                classInfo,
                document,
                schema
              );
              if (filter.showOnlyInvalid && validation.status === 'error')
                return true;
              if (filter.showOnlyWarnings && validation.status === 'warning')
                return true;
              return false;
            });
          }

          return objects;
        },

        // Helper: Get validation for a single object (memoization handled at component level)
        getValidationForObject: (object, classInfo, document, schema) => {
          return validateObject(object, classInfo, document, schema);
        },
      }),
      {
        name: 'visual-object-editor-storage',
        storage: createJSONStorage(() => localStorage, {
          replacer: setReplacer,
          reviver: setReviver,
        }),
        // Only persist UI preferences, not runtime state or selections
        partialize: (state) => ({
          viewMode: state.viewMode,
          expandedGroups: state.expandedGroups,
          expandedClasses: state.expandedClasses,
          autoValidate: state.autoValidate,
          filter: state.filter,
        }),
      }
    ),
    { name: 'VisualObjectEditorStore' }
  )
);

// Selector hooks
export const useSelectedObjects = () => {
  return useVisualObjectEditorStore((state) => state.selectedObjectIds);
};

export const useViewMode = () => {
  return useVisualObjectEditorStore((state) => state.viewMode);
};

export const useObjectFilter = () => {
  return useVisualObjectEditorStore((state) => state.filter);
};

export const useWizardState = () => {
  return useVisualObjectEditorStore(
    useShallow((state) => ({
      isOpen: state.wizardOpen,
      step: state.wizardStep,
      selectedClass: state.wizardSelectedClass,
      fieldValues: state.wizardFieldValues,
    }))
  );
};
